import { useGlobalStore } from "controller";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { activityPeriodOverrideAtom, dashboardScopeAtom } from "../store";
import type {
  DashboardScopeResource,
  DashboardScopeSelection,
  ReportResourceFilter,
} from "../types";

/**
 * Ids the selection names. "All" is sent as a keyword so it needs none, and
 * "Me" is empty until the global store resolves the signed-in resource.
 */
const toResourceIds = (
  selection: DashboardScopeSelection,
  loggedInResourceId: string | null | undefined,
): string[] => {
  if (selection.mode === "all") return [];
  if (selection.mode === "me") {
    return loggedInResourceId ? [loggedInResourceId] : [];
  }
  return selection.resources.map((resource) => resource.id);
};

/**
 * The dashboard's resource scope, translated into the shape each API expects.
 * Every Overview / Activity widget reads its filter from here — none of them
 * builds a `resource` param itself, because the four services behind the cards
 * all spell the filter differently.
 *
 * Cards must hold their query until `isReady`: firing early shows tenant-wide
 * numbers for a beat before the scoped ones replace them.
 */
export const useDashboardScope = () => {
  const loggedInResourceId = useGlobalStore((state) => state.resourceId);
  const [selection, setScope] = useAtom(dashboardScopeAtom);
  const setActivityPeriodOverride = useSetAtom(activityPeriodOverrideAtom);

  // Scope and period travel together: each scope reads best over its own range,
  // so applying a scope drops the range the user pinned and falls back to it.
  const setSelection = useCallback(
    (next: DashboardScopeSelection) => {
      setScope(next);
      setActivityPeriodOverride(null);
    },
    [setScope, setActivityPeriodOverride],
  );

  return useMemo(() => {
    const isAll = selection.mode === "all";
    const resources: DashboardScopeResource[] =
      selection.mode === "resources" ? selection.resources : [];
    const resourceIds = toResourceIds(selection, loggedInResourceId);

    return {
      mode: selection.mode,
      resources,
      resourceIds,
      /** `resource` for the report endpoints — the keyword for All, ids otherwise. */
      reportResource: (isAll ? "all" : resourceIds) as ReportResourceFilter,
      /**
       * `resource_id` for the QA service. It has no "all" keyword, so All means
       * "don't scope it" — which also pulls in unassigned work, unlike the
       * report endpoints.
       */
      qaResourceIds: isAll ? null : resourceIds,
      /**
       * Body for the V3 home dashboard. One form or the other, never both —
       * the server lets `all` win, but it shouldn't have to.
       */
      overviewBody: isAll
        ? { all: true as const }
        : { resource_ids: resourceIds },
      /** False until the scope actually names someone. */
      isReady: isAll || resourceIds.length > 0,
      setSelection,
    };
  }, [selection, loggedInResourceId, setSelection]);
};
